import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  qolAssessments: [],
  currentQolAssessment: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all QOL Assessments
export const fetchQolAssessments = createAsyncThunk(
  'qolAssessments/fetchQolAssessments',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/qol-assessments?patient_id=${patientId}`
        : '/qol-assessments';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch QOL assessments'
      );
    }
  }
);

// Fetch single QOL Assessment
export const fetchQolAssessment = createAsyncThunk(
  'qolAssessments/fetchQolAssessment',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/qol-assessments/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch QOL assessment'
      );
    }
  }
);

// Create QOL Assessment
export const createQolAssessment = createAsyncThunk(
  'qolAssessments/createQolAssessment',
  async (assessmentData, { rejectWithValue }) => {
    try {
      const response = await api.post('/qol-assessments', assessmentData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create QOL assessment'
      );
    }
  }
);

// Update QOL Assessment
export const updateQolAssessment = createAsyncThunk(
  'qolAssessments/updateQolAssessment',
  async ({ case_id, assessmentData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/qol-assessments/case/${case_id}`, assessmentData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update QOL assessment'
      );
    }
  }
);

// Delete QOL Assessment
export const deleteQolAssessment = createAsyncThunk(
  'qolAssessments/deleteQolAssessment',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/qol-assessments/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete QOL assessment'
      );
    }
  }
);

// ==================== Slice ====================
const qolAssessmentsSlice = createSlice({
  name: 'qolAssessments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentQolAssessment: (state, action) => {
      state.currentQolAssessment = action.payload;
    },
    clearCurrentQolAssessment: (state) => {
      state.currentQolAssessment = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchQolAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchQolAssessments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.qolAssessments = action.payload;
      })
      .addCase(fetchQolAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchQolAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchQolAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentQolAssessment = action.payload;
      })
      .addCase(fetchQolAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createQolAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createQolAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.qolAssessments.unshift(action.payload);
      })
      .addCase(createQolAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateQolAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateQolAssessment.fulfilled, (state, action) => {
        state.isLoading = false;

        const updated = action.payload.data ?? action.payload;
        const index = state.qolAssessments.findIndex(
          (item) => item.id === updated.id
        );

        if (index !== -1) {
          state.qolAssessments[index] = updated;
        }

        if (state.currentQolAssessment?.id === updated.id) {
          state.currentQolAssessment = updated;
        }
      })
      .addCase(updateQolAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteQolAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteQolAssessment.fulfilled, (state, action) => {
        state.isLoading = false;

        state.qolAssessments = state.qolAssessments.filter(
          (item) => item.id !== action.payload
        );

        if (state.currentQolAssessment?.id === action.payload) {
          state.currentQolAssessment = null;
        }
      })
      .addCase(deleteQolAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentQolAssessment,
  clearCurrentQolAssessment,
} = qolAssessmentsSlice.actions;

export default qolAssessmentsSlice.reducer;
